import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Config } from 'config/envConfig';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faBuilding,
    faGears,
    faKey,
    faPencil,
    faPlus,
    faSave,
    faShare,
    faSignOutAlt,
    faUpload,
    faUserCog,
    faUsers,
    faTrash,
    faInfoCircle,
    faEye,
} from '@fortawesome/free-solid-svg-icons';
import {
    alert,
    AlertType,
    Button,
    ConfirmModal,
    Dropdown,
    Input,
    Modal,
    TextArea,
    Toggle,
} from '@quyva/shared-ui';
import { message } from 'antd';
import { getUrl } from '../../../pdfDisplay/utils';
import OrganisationConfiguration from './OrganisationConfiguration';
import ViewOrganisationConfiguration from './ViewOrganisationConfiguration';
import { ManageOrganizationMembers } from './ManageOrganizationMembers';
import { CreateOrganizationForm } from './CreateOrganizationForm';
import { ApiKeyManagementContent } from '../apiKeySettings';
import { useSettingsContext, useGlobalCredits } from '../../../../context';

export const OrganizationSettings = ({
    SettingSections,
    organizationSettingsRef,
}) => {
    const {
        runWithSaving,
        saveOrganizationSection,
        isOrganizationDirty,
        accountDetails,
        setAccountDetails,
        organizationId,
        organizationName,
        setOrganizationName,
        organizationAddress,
        setOrganizationAddress,
        organizationIndustry,
        setOrganizationIndustry,
        organizationWebsite,
        setOrganizationWebsite,
        organizationOwner,
        userRole,
        setUserRole,
        fetchOrganizationDetails,
        orgQueryLimit,
        setOrgQueryLimit,
        isPostpaid,
        isSuperAdmin,
        orgCurrency,
        setOrgCurrency,
        orgPerUserCost,
        setOrgPerUserCost,
        supportedCurrencies,
        user,
        organizationTenantId,
        setOrganizationTenantId,
        teamsEnabled,
        setTeamsEnabled,
        teamsGreeting,
        setTeamsGreeting,
        loadOrganizationSection,
    } = useSettingsContext();
    const { isOrgAdmin } = useGlobalCredits();

    const orgLogoInputRef = useRef(null);
    const [organizationMode, setOrganizationMode] = useState(false);
    const [leaveOrganisationMode, setLeaveOrganisationMode] = useState(false);
    const [showCreateOrgModal, setShowCreateOrgModal] = useState(false);
    const [configureOrganisationMode, setConfigureOrganisationMode] =
        useState(false);
    const [configExists, setConfigExists] = useState(false);
    const [viewConfigMode, setViewConfigMode] = useState(false);
    const [showApiKeysModal, setShowApiKeysModal] = useState(false);

    const truncateText = (text, maxLength) => {
        return text.length > maxLength
            ? `${text.substring(0, maxLength)}...`
            : text;
    };

    const handleOrgBrandingFieldUpdate = (fieldName, value) => {
        const next = { ...(accountDetails || {}) };
        next.vendorDetails = { ...(next.vendorDetails || {}) };
        next.vendorDetails[fieldName] = value;
        setAccountDetails(next);
    };

    const uploadOrganisationConfig = async () => {
        try {
            const fileInput = document.createElement('input');
            fileInput.type = 'file';
            fileInput.accept = '.json';

            fileInput.onchange = async (event) => {
                const file = event.target.files[0];
                if (!file) return;

                const formData = new FormData();
                formData.append('file', file);
                formData.append('organizationId', organizationId);

                const dynamicUrl = getUrl(
                    `http://${Config.FAST_API_HOST}:${Config.FAST_API_PORT}/organisation_configuration_file`
                );

                try {
                    await axios.post(dynamicUrl, formData, {
                        headers: {
                            'Content-Type': 'multipart/form-data',
                        },
                    });
                    message.success('File uploaded successfully.');
                } catch (uploadError) {
                    console.error('❌ Upload failed:', uploadError);
                    message.error('File upload failed. Please try again.');
                }
            };

            fileInput.click();
        } catch (error) {
            console.error('Error preparing upload:', error);
        }
    };

    const checkExistingConfig = async () => {
        try {
            const dynamicUrl = getUrl(
                `http://${Config.FAST_API_HOST}:${Config.FAST_API_PORT}/check_organisation_configuration_file?organizationId=${organizationId}`
            );

            const response = await axios.get(dynamicUrl);
            setConfigExists(response.data.exists);
        } catch (error) {
            console.error('Error checking existing configuration:', error);
        }
    };

    useEffect(() => {
        if (organizationId) checkExistingConfig();
    }, [organizationId]);

    useEffect(() => {
        loadOrganizationSection();
    }, []);

    const handleLeaveOrganization = async () => {
        try {
            if (userRole === 'admin' && organizationOwner === user.email) {
                alert(
                    AlertType.ERROR,
                    'As the organization owner, you cannot leave the organization. Please contact support for further assistance.'
                );
                setLeaveOrganisationMode(false);
                return;
            }

            const authToken = encodeURIComponent(user.token);
            let response;
            try {
                response = await axios.post(
                    getUrl(
                        `http://${Config.NODE_SERVER_HOST}:${Config.NODE_SERVER_PORT}/organization/leave`
                    ),
                    {
                        email: user.email,
                    },
                    {
                        headers: {
                            'Content-Type': 'application/json',
                            Authorization: authToken,
                        },
                    }
                );
            } catch (e) {
                const payload = e?.response?.data;
                throw new Error(
                    payload?.message ||
                        payload?.detail ||
                        'Failed to leave organization'
                );
            }

            if (!response || response.status < 200 || response.status >= 300) {
                throw new Error('Failed to leave organization');
            }

            setOrganizationName(null);
            setOrganizationAddress(null);
            setOrganizationWebsite(null);
            setUserRole(null);
            setLeaveOrganisationMode(false);
            alert(
                AlertType.SUCCESS,
                `You have successfully left ${organizationName}`
            );
        } catch (error) {
            console.error('Error leaving organization:', error);
            alert(AlertType.ERROR, 'Failed to leave organization');
        }
    };

    const handleCreateOrgSuccess = async (orgData) => {
        setOrganizationName(orgData.name);
        setOrganizationAddress(orgData.address);
        setOrganizationWebsite(orgData.website);

        await fetchOrganizationDetails();
        setShowCreateOrgModal(false);

        alert(AlertType.SUCCESS, 'Organization created successfully');
    };

    const handleTenantIdChange = (e) => {
        const value = e.target.value.trim();
        setOrganizationTenantId(value);
    };

    return (
        <>
            <div ref={organizationSettingsRef}>
                <div className='flex items-center justify-between font-semibold mb-2 py-3 px-4 text-texts-secondary300 uppercase bg-backgrounds-primary100 truncate'>
                    <div className='flex items-center'>
                        <FontAwesomeIcon icon={faUsers} className='mr-2' />
                        <div className='font-semibold text-texts-secondary300 uppercase truncate'>
                            {SettingSections.ORGANIZATION}
                        </div>
                    </div>

                    <div className='flex items-center gap-2 shrink-0'>
                        {organizationName != null && (
                            <>
                                {userRole === 'admin' && (
                                    <>
                                        <Button
                                            hoverText={`${
                                                configExists
                                                    ? 'Update'
                                                    : 'Upload'
                                            } Organization Data`}
                                            icon={
                                                configExists
                                                    ? faPencil
                                                    : faUpload
                                            }
                                            onClick={async () => {
                                                await uploadOrganisationConfig();
                                                if (organizationId)
                                                    await checkExistingConfig();
                                            }}
                                            type='success'
                                        />

                                        {configExists && (
                                            <Button
                                                hoverText='View uploaded approved contacts (read-only)'
                                                icon={faEye}
                                                onClick={() =>
                                                    setViewConfigMode(true)
                                                }
                                                type='success'
                                            />
                                        )}

                                        <Button
                                            hoverText='Setup Data Access Control Rules'
                                            icon={faGears}
                                            onClick={() =>
                                                setConfigureOrganisationMode(
                                                    true
                                                )
                                            }
                                            type='success'
                                        />

                                        <Button
                                            hoverText='Edit Organization Members'
                                            icon={faUserCog}
                                            onClick={() =>
                                                setOrganizationMode(true)
                                            }
                                            type='success'
                                        />

                                        <Button
                                            hoverText='Manage Organization API Keys'
                                            icon={faKey}
                                            onClick={() => setShowApiKeysModal(true)}
                                            type='success'
                                        />
                                    </>
                                )}

                                <Button
                                    icon={faSignOutAlt}
                                    onClick={() =>
                                        setLeaveOrganisationMode(true)
                                    }
                                    hoverText='Leave Organization'
                                    type='danger'
                                />
                            </>
                        )}

                        <Button
                            type='success'
                            icon={faSave}
                            text={'Save'}
                            disabled={!isOrganizationDirty}
                            hoverText='Save organization settings'
                            onClick={() =>
                                runWithSaving(async () => {
                                    await saveOrganizationSection();
                                })
                            }
                        />
                    </div>
                </div>

                <div className='h-[calc(100vh-210px)] w-full overflow-auto p-6'>
                    {organizationName == null ? (
                        <div className='flex flex-col items-center justify-center p-8'>
                            <p className='text-texts-secondary300 mb-4'>
                                You are not part of any organization
                            </p>
                            <Button
                                text='Create Organization'
                                icon={faPlus}
                                type='success'
                                onClick={() => setShowCreateOrgModal(true)}
                            />
                        </div>
                    ) : (
                        <>
                            <div className='mt-2'>
                                <div className='flex items-center mb-6'>
                                    <div className='w-1/4 font-semibold text-texts-secondary300'>
                                        <label>Organization Name</label>
                                    </div>
                                    <div className='w-full md:w-1/4'>
                                        <Input
                                            type='text'
                                            placeholder='Name'
                                            value={organizationName || ''}
                                            onChange={(e) =>
                                                setOrganizationName(
                                                    e.target.value
                                                )
                                            }
                                            disabled={userRole !== 'admin'}
                                        />
                                    </div>
                                </div>

                                <div className='flex items-center mb-6'>
                                    <div className='w-1/4 font-semibold text-texts-secondary300'>
                                        <label>Company Logo</label>
                                    </div>
                                    <div className='w-full md:w-1/4'>
                                        <div className='flex items-center gap-4'>
                                            <div
                                                className='relative rounded-xl border flex items-center justify-center bg-backgrounds-white p-2'
                                                style={{
                                                    width: '120px',
                                                    height: '120px',
                                                }}
                                            >
                                                {accountDetails?.vendorDetails
                                                    ?.logo ? (
                                                    <img
                                                        src={URL.createObjectURL(
                                                            accountDetails
                                                                .vendorDetails
                                                                .logo instanceof
                                                                Blob
                                                                ? accountDetails
                                                                      .vendorDetails
                                                                      .logo
                                                                : new Blob([
                                                                      accountDetails
                                                                          .vendorDetails
                                                                          .logo,
                                                                  ])
                                                        )}
                                                        alt='Organization Logo'
                                                        className='rounded-xl w-full h-full object-contain'
                                                    />
                                                ) : (
                                                    <FontAwesomeIcon
                                                        icon={faBuilding}
                                                        size='3x'
                                                        className='text-texts-secondary300 rounded-xl p-6 bg-backgrounds-white'
                                                    />
                                                )}
                                            </div>

                                            <div className='flex flex-col gap-2'>
                                                <input
                                                    ref={orgLogoInputRef}
                                                    type='file'
                                                    accept='image/*'
                                                    className='hidden'
                                                    onChange={(e) => {
                                                        const file =
                                                            e.target.files?.[0];
                                                        if (file) {
                                                            handleOrgBrandingFieldUpdate(
                                                                'logo',
                                                                file
                                                            );
                                                        }
                                                    }}
                                                />
                                                <Button
                                                    text='Edit'
                                                    icon={faPencil}
                                                    type='success'
                                                    disabled={
                                                        userRole !== 'admin'
                                                    }
                                                    hoverText='Edit logo'
                                                    onClick={() => {
                                                        if (
                                                            orgLogoInputRef.current
                                                        ) {
                                                            orgLogoInputRef.current.click();
                                                        }
                                                    }}
                                                />
                                                <Button
                                                    text='Remove'
                                                    icon={faTrash}
                                                    type='danger'
                                                    disabled={
                                                        userRole !== 'admin' ||
                                                        !accountDetails
                                                            ?.vendorDetails
                                                            ?.logo
                                                    }
                                                    hoverText='Remove logo'
                                                    onClick={() => {
                                                        handleOrgBrandingFieldUpdate(
                                                            'logo',
                                                            null
                                                        );
                                                        if (
                                                            orgLogoInputRef.current
                                                        ) {
                                                            orgLogoInputRef.current.value =
                                                                '';
                                                        }
                                                    }}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className='flex items-center mb-6'>
                                    <div className='w-1/4 font-semibold text-texts-secondary300'>
                                        <label>Copyright Text</label>
                                    </div>
                                    <div className='w-full md:w-1/4'>
                                        <Input
                                            type='text'
                                            placeholder='Copyright Text (Optional)'
                                            value={
                                                accountDetails?.vendorDetails
                                                    ?.copy_right_text || ''
                                            }
                                            onChange={(e) =>
                                                handleOrgBrandingFieldUpdate(
                                                    'copy_right_text',
                                                    e.target.value
                                                )
                                            }
                                            disabled={userRole !== 'admin'}
                                        />
                                    </div>
                                </div>

                                <div className='flex items-center mb-6'>
                                    <div className='w-1/4 font-semibold text-texts-secondary300'>
                                        <label>Website</label>
                                    </div>
                                    <div className='w-full md:w-1/4'>
                                        <Input
                                            type='text'
                                            placeholder='Website (Optional)'
                                            value={organizationWebsite || ''}
                                            onChange={(e) =>
                                                setOrganizationWebsite(
                                                    e.target.value
                                                )
                                            }
                                            disabled={userRole !== 'admin'}
                                        />
                                    </div>
                                </div>

                                <div className='flex items-center mb-6'>
                                    <div className='w-1/4 font-semibold text-texts-secondary300'>
                                        <label>Address</label>
                                    </div>
                                    <div className='w-full md:w-1/4'>
                                        <TextArea
                                            className='w-full rounded-xl p-2 hover:outline-none focus:outline-backgrounds-primary200 border hover:border-backgrounds-primary300'
                                            placeholder='Address (Optional)'
                                            value={organizationAddress || ''}
                                            onChange={(e) =>
                                                setOrganizationAddress(
                                                    e.target.value
                                                )
                                            }
                                            disabled={userRole !== 'admin'}
                                            rows={3}
                                        />
                                    </div>
                                </div>

                                <div className='flex items-center mb-6'>
                                    <div className='w-1/4 font-semibold text-texts-secondary300'>
                                        <label>Industry</label>
                                    </div>
                                    <div className='w-full md:w-1/4'>
                                        <Input
                                            type='text'
                                            placeholder='Industry (Optional)'
                                            value={organizationIndustry || ''}
                                            onChange={(e) =>
                                                setOrganizationIndustry(
                                                    e.target.value
                                                )
                                            }
                                            disabled={userRole !== 'admin'}
                                        />
                                    </div>
                                </div>

                                {isOrgAdmin && isPostpaid && (
                                    <div className='flex items-center mb-6'>
                                        <div className='w-1/4 font-semibold text-texts-secondary300'>
                                            <label>Per-Member Query Limit</label>
                                        </div>
                                        <div className='w-full md:w-1/4'>
                                            <Input
                                                type='number'
                                                value={orgQueryLimit}
                                                onChange={(e) =>
                                                    setOrgQueryLimit(
                                                        e.target.value
                                                    )
                                                }
                                                placeholder='Enter query limit'
                                                min={1}
                                            />
                                        </div>
                                    </div>
                                )}

                                {(userRole === 'admin' || isSuperAdmin) && isPostpaid && (
                                    <div className='flex items-center mb-6'>
                                        <div className='w-1/4 font-semibold text-texts-secondary300'>
                                            <label>Billing Currency</label>
                                        </div>
                                        <div className='w-full md:w-1/4'>
                                            <Dropdown
                                                dropDownList={supportedCurrencies.map((c) => ({
                                                    label: c,
                                                    action: () => setOrgCurrency(c),
                                                    selected: (orgCurrency || 'USD') === c,
                                                }))}
                                                buttonText={orgCurrency || 'USD'}
                                                showButtonText={true}
                                                listTitleText='Billing Currency'
                                                placement='bottomLeft'
                                                buttonClassName='w-full'
                                            />
                                        </div>
                                    </div>
                                )}

                                {isSuperAdmin && isPostpaid && (
                                    <div className='flex items-center mb-6'>
                                        <div className='w-1/4 font-semibold text-texts-secondary300'>
                                            <label>Per User Cost / Month</label>
                                        </div>
                                        <div className='w-full md:w-1/4'>
                                            <Input
                                                type='number'
                                                value={orgPerUserCost ?? ''}
                                                onChange={(e) => setOrgPerUserCost(e.target.value)}
                                                placeholder={`Amount in ${orgCurrency || 'USD'}`}
                                                min={0}
                                                step='0.01'
                                            />
                                        </div>
                                    </div>
                                )}
                            </div>

                            <div className='flex items-center mb-6'>
                                <div className='w-1/4 font-semibold text-texts-secondary300'>
                                    <label>Microsoft Teams Access</label>
                                </div>
                                <div
                                    className='w-full md:w-1/4'
                                    title={
                                        !teamsEnabled
                                            ? 'Enable to give microsoft teams access to your organization user'
                                            : 'Disabled teams access organization wide'
                                    }
                                >
                                    <Toggle
                                        // toggleText='Organization Teams Access'
                                        toggleTextClassName='font-semibold text-texts-secondary300'
                                        checked={teamsEnabled}
                                        onChange={(isChecked) => {
                                            setTeamsEnabled(isChecked);
                                        }}
                                        disabled={userRole !== 'admin'}
                                    />
                                </div>
                            </div>

                            {teamsEnabled && (
                                <div className='flex items-center mb-6'>
                                    <div className='w-1/4 font-semibold text-texts-secondary300'>
                                        <label>Azure AD Tenant ID</label>
                                    </div>
                                    <div className='w-full md:w-1/4'>
                                        <Input
                                            type='text'
                                            placeholder='Required'
                                            value={organizationTenantId}
                                            onChange={handleTenantIdChange}
                                            disabled={userRole !== 'admin'}
                                        />
                                    </div>
                                    <FontAwesomeIcon
                                        icon={faInfoCircle}
                                        title='This is your organization Azure AD Tenant ID used for enabling team and user provisioning. This can be found at portal.azure.com under Azure Active Directory > Overview.'
                                        className='ml-2 cursor-pointer text-texts-tertiary'
                                    />
                                </div>
                            )}

                            {teamsEnabled && (
                                <div className='flex items-start mb-6'>
                                    <div className='w-1/4 font-semibold text-texts-secondary300 pt-1'>
                                        <label>Teams Greeting Message</label>
                                        <p className='text-xs font-normal text-texts-secondary300 mt-1'>
                                            Sent when a user is added to a Teams conversation with the bot. Leave blank to auto-generate from bot persona.
                                        </p>
                                    </div>
                                    <div className='w-full md:w-2/4 flex flex-col gap-2'>
                                        <textarea
                                            className='w-full rounded border border-backgrounds-primary200 bg-backgrounds-white text-texts-secondary300 px-3 py-2 text-sm resize-y focus:outline-none focus:ring-1 focus:ring-buttons-primary'
                                            rows={3}
                                            maxLength={500}
                                            value={teamsGreeting}
                                            placeholder="e.g. Good day! How can I assist you today?"
                                            disabled={userRole !== 'admin'}
                                            onChange={(e) => setTeamsGreeting(e.target.value)}
                                        />
                                        <span className='text-xs text-texts-secondary300 self-end'>
                                            {teamsGreeting.length}/500
                                        </span>
                                    </div>
                                </div>
                            )}

                            {configureOrganisationMode && (
                                <Modal
                                    title={
                                        <span title={organizationName}>
                                            {`Access Control (${truncateText(
                                                organizationName,
                                                20
                                            )})`}
                                        </span>
                                    }
                                    titleIcon={faGears}
                                    iconColor='sky'
                                    onClose={() =>
                                        setConfigureOrganisationMode(false)
                                    }
                                >
                                    <OrganisationConfiguration
                                        setConfigureOrganisationMode={
                                            setConfigureOrganisationMode
                                        }
                                    ></OrganisationConfiguration>
                                </Modal>
                            )}

                            {viewConfigMode && (
                                <Modal
                                    title={
                                        <span title={organizationName}>
                                            {`Approved Contacts (${truncateText(
                                                organizationName,
                                                20
                                            )})`}
                                        </span>
                                    }
                                    titleIcon={faEye}
                                    iconColor='sky'
                                    onClose={() => setViewConfigMode(false)}
                                >
                                    <ViewOrganisationConfiguration
                                        organizationId={organizationId}
                                    />
                                </Modal>
                            )}

                            {organizationMode && (
                                <Modal
                                    title={
                                        <span title={organizationName}>
                                            {`Manage '${truncateText(
                                                organizationName,
                                                20
                                            )}'`}
                                        </span>
                                    }
                                    titleIcon={faShare}
                                    iconColor='sky'
                                    onClose={() => setOrganizationMode(false)}
                                >
                                    <ManageOrganizationMembers
                                        user={user}
                                        setViewOrganizationModal={
                                            setOrganizationMode
                                        }
                                    />
                                </Modal>
                            )}

                            {showApiKeysModal && (
                                <Modal
                                    title='API Key Management'
                                    titleIcon={faKey}
                                    iconColor='sky'
                                    onClose={() => setShowApiKeysModal(false)}
                                >
                                    <ApiKeyManagementContent />
                                </Modal>
                            )}

                            {leaveOrganisationMode && (
                                <ConfirmModal
                                    modalText={`Leave '${organizationName}' Organization?`}
                                    onConfirm={handleLeaveOrganization}
                                    onCancel={() =>
                                        setLeaveOrganisationMode(false)
                                    }
                                />
                            )}
                        </>
                    )}
                </div>

                {showCreateOrgModal && (
                    <Modal
                        title='Create Organization'
                        titleIcon={faUsers}
                        iconColor='sky'
                        onClose={() => setShowCreateOrgModal(false)}
                        className='w-[50vw]'
                    >
                        <CreateOrganizationForm
                            onClose={() => setShowCreateOrgModal(false)}
                            onSuccess={handleCreateOrgSuccess}
                        />
                    </Modal>
                )}
            </div>
        </>
    );
};
